import React, { useState, useEffect } from 'react';
import {
  ArrowLeft,
  Settings,
  Heart,
  MessageCircle,
  Share,
  MoreHorizontal,
  Trophy,
  Award,
  Medal,
  Star,
  Calendar,
  MapPin,
  UserPlus,
  UserCheck,
  Users,
  Grid3X3
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useParams, useNavigate, Link, useLocation } from 'react-router-dom';

import { Camera, Plus, X as XIcon, Save } from 'lucide-react';
import { uploadService } from '../../services/api';
import api from '../../services/api';
import toast from 'react-hot-toast';
import { getSportIcon } from '../../utils/sportUtils';
import CommentsSection from '../../components/common/CommentsSection';
import ShareModal from '../../components/common/ShareModal';


interface UserProfile {
  id: string;
  _id: string;
  name: string;
  firstName?: string;
  lastName?: string;
  role: string;
  sport: string;
  sports: string[];
  avatar: string | null;
  bio: string;
  location: string;
  joinedDate: string;
  stats: {
    followers: number;
    following: number;
    posts: number;
  };
  isFollowing: boolean;
  isOwnProfile: boolean;
  badges: any[];
}

interface UserPost {
  id: string;
  user: {
    name: string;
    role: string;
    sport: string;
    avatar: string | null;
  };
  content: string;
  image: string | null;
  timeAgo: string;
  likes: number;
  comments: number;
  shares: number;
  isLiked: boolean;
  location?: string;
  tags?: any[];
}

// Mock user data
const mockUserProfile = {
  id: '',
  _id: '',
  name: '',
  firstName: '',
  lastName: '',
  role: '',
  sport: '',
  sports: [] as string[],
  avatar: null,
  bio: '',
  location: '',
  joinedDate: '',
  stats: {
    followers: 0,
    following: 0,
    posts: 0
  },
  isFollowing: false,
  isOwnProfile: false,
  badges: [] as any[]
};

// Empty initial data - will be populated from API
const mockUserPosts: UserPost[] = [];

// Empty initial achievements
const mockAchievements: any[] = [];

// Empty initial certificates
const mockCertificates: any[] = [];

// Achievement Card Component with Flip Animation and Image Support
interface AchievementCardProps {
  achievement: {
    id: string;
    title: string;
    description: string;
    sport?: string;
    type?: string;
    level?: string;
    date: string;
    verified?: boolean;
    images?: { url: string; caption?: string }[];
    proofDocuments?: { url: string; type?: string; description?: string }[];
  };
  formatDate: (dateString: string) => string;
}

const AchievementCard: React.FC<AchievementCardProps> = ({ achievement, formatDate }) => {
  const [isFlipped, setIsFlipped] = useState(false);

  const isGold = achievement.level === 'gold';
  const isSilver = achievement.level === 'silver';
  const isInternational = achievement.level === 'international' || achievement.level === 'professional';
  const isNational = achievement.level === 'national';

  const cardColor = isGold || isInternational
    ? 'from-yellow-400 via-yellow-500 to-yellow-600'
    : isSilver || isNational
    ? 'from-gray-300 via-gray-400 to-gray-500'
    : achievement.level === 'bronze' || achievement.level === 'regional'
    ? 'from-orange-400 via-orange-500 to-orange-600'
    : 'from-blue-400 via-blue-500 to-blue-600';

  const levelBadgeClass = isGold || isInternational ? 'bg-yellow-700 text-yellow-100'
    : isSilver || isNational ? 'bg-gray-700 text-gray-100'
    : achievement.level === 'bronze' || achievement.level === 'regional' ? 'bg-orange-700 text-orange-100'
    : 'bg-blue-700 text-blue-100';

  // Get image URL from images array or proof documents
  const imageUrl = achievement.images && achievement.images.length > 0
    ? achievement.images[0].url
    : achievement.proofDocuments && achievement.proofDocuments.length > 0 && achievement.proofDocuments[0].type === 'photo'
    ? achievement.proofDocuments[0].url
    : null;

  return (
    <div
      className="relative h-72 cursor-pointer group"
      onClick={() => setIsFlipped(!isFlipped)}
      style={{ perspective: '1000px' }}
    >
      <div
        className="relative w-full h-full transition-transform duration-700"
        style={{
          transformStyle: 'preserve-3d',
          transform: isFlipped ? 'rotateY(180deg)' : 'rotateY(0deg)'
        }}
      >
        {/* Front Side */}
        <div
          className={`absolute inset-0 w-full h-full rounded-2xl shadow-xl overflow-hidden ${!imageUrl ? `bg-gradient-to-br ${cardColor}` : ''}`}
          style={{ backfaceVisibility: 'hidden' }}
        >
          {imageUrl ? (
            <>
              <img src={imageUrl} alt={achievement.title} className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent"></div>
              <div className="absolute bottom-0 left-0 right-0 p-5 text-white">
                <div className="flex items-center justify-between mb-2">
                  <h4 className="font-bold text-lg drop-shadow-md">{achievement.title}</h4>
                  {achievement.verified && (
                    <div className="w-6 h-6 bg-green-500 rounded-full flex items-center justify-center shadow-md">
                      <span className="text-white text-xs font-bold">✓</span>
                    </div>
                  )}
                </div>
                {achievement.level && (
                  <span className={`px-3 py-1 rounded-full text-xs font-bold uppercase ${levelBadgeClass}`}>
                    {achievement.level}
                  </span>
                )}
                <p className="text-xs text-white/80 mt-2 animate-pulse">Click to view details →</p>
              </div>
            </>
          ) : (
            <>
              {/* Decorative Elements */}
              <div className="absolute top-0 right-0 w-32 h-32 bg-white opacity-10 rounded-full -mr-16 -mt-16"></div>
              <div className="absolute bottom-0 left-0 w-24 h-24 bg-white opacity-10 rounded-full -ml-12 -mb-12"></div>

              <div className="relative z-10 flex flex-col items-center justify-center h-full text-center p-6 text-white">
                <div className="p-4 rounded-xl bg-white bg-opacity-20 backdrop-blur-sm mb-4 transform group-hover:scale-110 transition-transform duration-300">
                  <Trophy className="w-10 h-10 text-white" />
                </div>
                <h4 className="font-bold text-lg mb-2 drop-shadow-md">{achievement.title}</h4>
                <p className="text-sm text-white/80 line-clamp-2 mb-3">{achievement.description}</p>
                {achievement.verified && (
                  <div className="flex items-center space-x-2 px-3 py-1 bg-white/20 rounded-full mb-2">
                    <span className="text-xs font-bold">✓ VERIFIED</span>
                  </div>
                )}
                {achievement.level && (
                  <span className={`px-3 py-1 rounded-full text-xs font-bold uppercase ${levelBadgeClass}`}>
                    {achievement.level}
                  </span>
                )}
                <p className="text-xs text-white/70 mt-3 animate-pulse">Click to view details →</p>
              </div>
            </>
          )}
        </div>

        {/* Back Side - Detailed View */}
        <div
          className={`absolute inset-0 w-full h-full p-6 bg-gradient-to-br ${cardColor} rounded-2xl shadow-xl`}
          style={{
            backfaceVisibility: 'hidden',
            transform: 'rotateY(180deg)'
          }}
        >
          <div className="flex flex-col h-full text-white">
            <div className="flex items-start justify-between mb-3">
              <h4 className="font-bold text-lg">{achievement.title}</h4>
              {achievement.verified && (
                <div className="w-6 h-6 bg-white rounded-full flex items-center justify-center shadow-md flex-shrink-0">
                  <span className="text-green-600 text-xs font-bold">✓</span>
                </div>
              )}
            </div>

            <div className="flex-1 mb-3">
              <p className="text-sm text-white/90 leading-relaxed">{achievement.description}</p>
            </div>

            <div className="space-y-2 border-t border-white/30 pt-3">
              {achievement.sport && (
                <div className="flex items-center space-x-2">
                  <span className="font-bold text-xs text-white/70">SPORT:</span>
                  <span className="text-sm capitalize">{achievement.sport}</span>
                </div>
              )}
              {achievement.type && (
                <div className="flex items-center space-x-2">
                  <span className="font-bold text-xs text-white/70">TYPE:</span>
                  <span className="text-sm capitalize">{achievement.type}</span>
                </div>
              )}
              <div className="flex items-center space-x-2">
                <Calendar className="w-4 h-4 text-white/70" />
                <span className="text-sm font-medium">{formatDate(achievement.date)}</span>
              </div>
              {achievement.level && (
                <div className="flex items-center space-x-2">
                  <span className={`px-3 py-1 rounded-full text-xs font-bold uppercase ${levelBadgeClass}`}>
                    {achievement.level}
                  </span>
                </div>
              )}
            </div>

            <p className="text-xs text-white/70 font-medium text-center mt-3 animate-pulse">Click to flip back ←</p>
          </div>
        </div>
      </div>
    </div>
  );
};

// Certificate Card Component with Flip Animation and Image Support
interface CertificateCardProps {
  certificate: {
    id: string;
    title: string;
    description: string;
    issuer: string;
    date: string;
    verified: boolean;
    type?: string;
    images?: { url: string; caption?: string }[];
    image?: string;
  };
  formatDate: (dateString: string) => string;
}

const CertificateCard: React.FC<CertificateCardProps> = ({ certificate, formatDate }) => {
  const [isFlipped, setIsFlipped] = useState(false);

  // Get image URL from images array or direct image field
  const imageUrl = certificate.images && certificate.images.length > 0
    ? certificate.images[0].url
    : certificate.image || null;

  return (
    <div
      className="relative h-64 cursor-pointer group"
      onClick={() => setIsFlipped(!isFlipped)}
      style={{ perspective: '1000px' }}
    >
      <div
        className="relative w-full h-full transition-transform duration-700"
        style={{
          transformStyle: 'preserve-3d',
          transform: isFlipped ? 'rotateY(180deg)' : 'rotateY(0deg)'
        }}
      >
        {/* Front Side */}
        <div
          className={`absolute inset-0 w-full h-full rounded-2xl shadow-2xl overflow-hidden ${!imageUrl ? 'bg-gradient-to-br from-amber-50 via-yellow-50 to-amber-100 border-4 border-amber-300' : ''}`}
          style={{ backfaceVisibility: 'hidden' }}
        >
          {imageUrl ? (
            <>
              <img src={imageUrl} alt={certificate.title} className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent"></div>
              <div className="absolute bottom-0 left-0 right-0 p-5 text-white">
                <div className="flex items-center justify-between mb-1">
                  <h4 className="font-bold text-lg drop-shadow-md">{certificate.title}</h4>
                  {certificate.verified && (
                    <div className="w-6 h-6 bg-green-500 rounded-full flex items-center justify-center shadow-md">
                      <span className="text-white text-xs font-bold">✓</span>
                    </div>
                  )}
                </div>
                <p className="text-sm text-white/80">{certificate.issuer}</p>
                <p className="text-xs text-white/60 mt-2 animate-pulse">Click to view details →</p>
              </div>
            </>
          ) : (
            <>
              {/* Decorative Border Pattern */}
              <div className="absolute inset-2 border-2 border-amber-400 rounded-xl opacity-30"></div>
              
              <div className="relative z-10 flex flex-col items-center justify-center h-full text-center p-6">
                {/* Shine Effect */}
                <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-br from-transparent via-white to-transparent opacity-20 rounded-2xl"></div>
                
                <div className="relative z-10 p-4 bg-gradient-to-br from-amber-400 to-yellow-500 rounded-full mb-4 shadow-xl transform group-hover:scale-110 transition-transform duration-300">
                  <Award className="w-10 h-10 text-white" />
                </div>
                
                <h4 className="font-bold text-gray-900 text-xl mb-3 drop-shadow-sm">{certificate.title}</h4>
                
                {certificate.verified && (
                  <div className="flex items-center space-x-2 px-3 py-1 bg-green-500 rounded-full mb-3 shadow-md">
                    <div className="w-4 h-4 bg-white rounded-full flex items-center justify-center">
                      <span className="text-green-600 text-xs font-bold">✓</span>
                    </div>
                    <span className="text-xs font-bold text-white">VERIFIED</span>
                  </div>
                )}
                
                <p className="text-xs text-amber-700 font-medium mt-2 animate-pulse">Click to view details →</p>
              </div>
            </>
          )}
        </div>

        {/* Back Side - Detailed View */}
        <div
          className="absolute inset-0 w-full h-full p-6 bg-gradient-to-br from-amber-100 via-yellow-100 to-amber-200 border-4 border-amber-400 rounded-2xl shadow-2xl"
          style={{
            backfaceVisibility: 'hidden',
            transform: 'rotateY(180deg)'
          }}
        >
          <div className="flex flex-col h-full">
            <div className="flex items-center justify-between mb-4">
              <h4 className="font-bold text-gray-900 text-lg">{certificate.title}</h4>
              {certificate.verified && (
                <div className="w-6 h-6 bg-green-500 rounded-full flex items-center justify-center shadow-md">
                  <span className="text-white text-xs font-bold">✓</span>
                </div>
              )}
            </div>
            
            <div className="flex-1 mb-4">
              <p className="text-sm text-gray-700 font-medium leading-relaxed">{certificate.description}</p>
            </div>
            
            <div className="space-y-2 border-t-2 border-amber-300 pt-3">
              <div className="flex items-center space-x-2">
                <span className="font-bold text-gray-800 text-xs">ISSUER:</span>
                <span className="text-sm text-gray-700">{certificate.issuer}</span>
              </div>
              <div className="flex items-center space-x-2">
                <Calendar className="w-4 h-4 text-amber-600" />
                <span className="text-sm text-gray-700 font-medium">{formatDate(certificate.date)}</span>
              </div>
            </div>
            
            <p className="text-xs text-amber-700 font-medium text-center mt-3 animate-pulse">Click to flip back ←</p>
          </div>
        </div>
      </div>
    </div>
  );
};

const ProfilePage: React.FC = () => {
  const { user: currentUser, setUserData } = useAuth();
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const [userProfile, setUserProfile] = useState<UserProfile>(mockUserProfile as UserProfile);
  const [posts, setPosts] = useState<UserPost[]>(mockUserPosts as UserPost[]);
  const [achievements, setAchievements] = useState<any[]>(mockAchievements);
  const [certificates, setCertificates] = useState<any[]>(mockCertificates);

  const [activeTab, setActiveTab] = useState<'posts' | 'achievements' | 'certificates'>('posts');
  const [loading, setLoading] = useState(false);
  const [followStatus, setFollowStatus] = useState<{
    followStatus: string;
    isFollowing: boolean;
    hasPendingRequest: boolean;
    isRequestSentByMe: boolean;
    isRequestReceived: boolean;
  } | null>(null);
  
  const [showEditModal, setShowEditModal] = useState(false);

  useEffect(() => {
    const editParam = new URLSearchParams(location.search).get('edit');
    if (editParam === 'true' && userProfile.isOwnProfile && !loading) {
      setShowEditModal(true);
      // Remove query param without refreshing to avoid re-opening on manual refresh
      navigate(location.pathname, { replace: true });
    }
  }, [location.search, userProfile.isOwnProfile, loading, location.pathname, navigate]);
  const [showFollowersModal, setShowFollowersModal] = useState<{ show: boolean; type: 'followers' | 'following' }>({ show: false, type: 'followers' });
  const [networkUsers, setNetworkUsers] = useState<any[]>([]);
  const [isUpdatingProfile, setIsUpdatingProfile] = useState(false);
  const [editForm, setEditForm] = useState({
    firstName: '',
    lastName: '',
    bio: '',
    location: '',
    sports: [] as string[]
  });
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const [showComments, setShowComments] = useState<string | null>(null);
  const [showShareModal, setShowShareModal] = useState<string | null>(null);
  const fileInputRef = React.useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (id) {
      loadUserProfile(id);
      loadFollowStatus(id);
    }
  }, [id]);

  const loadFollowStatus = async (userId: string) => {
    try {
      const response = await api.get(`/users/${userId}/follow-status`);
      if (response.data.success) {
        setFollowStatus(response.data.data);
      }
    } catch (error) {
      console.error('Failed to load follow status:', error);
    }
  };

  // Refresh profile when posts change
  const refreshProfile = () => {
    if (id) {
      loadUserProfile(id);
    }
  };

  const loadUserProfile = async (userId: string) => {
    try {
      setLoading(true);
      
      // Load profile data
      try {
        const profileRes = await api.get(`/users/${userId}`);
        if (profileRes.data.success) {
          const profileData = profileRes.data.data.user;
          setUserProfile(profileData);
          setEditForm({
            firstName: profileData.firstName || '',
            lastName: profileData.lastName || '',
            bio: profileData.bio || '',
            location: profileData.location || '',
            sports: profileData.sports || []
          });
        }
      } catch (err) {
        console.error('Failed to load profile data:', err);
        // Fallback or handle error
        setUserProfile((prev: UserProfile) => ({
          ...prev,
          name: currentUser?.name || prev.name,
          role: currentUser?.role || prev.role,
          isOwnProfile: currentUser?._id === userId
        }));
      }

      // Load posts
      try {
        const postsRes = await api.get(`/users/${userId}/posts`);
        if (postsRes.data.success) {
          setPosts(postsRes.data.data.posts);
          // Sync post count in profile
          setUserProfile(prev => ({
            ...prev,
            stats: {
              ...prev.stats,
              posts: postsRes.data.data.posts.length
            }
          }));
        }
      } catch (err) {
        console.error('Failed to load posts:', err);
      }

      // Load achievements
      try {
        const achievementsRes = await api.get(`/users/${userId}/achievements`);
        if (achievementsRes.data.success) {
          setAchievements(achievementsRes.data.data.achievements);
        }
      } catch (err) {
        console.error('Failed to load achievements:', err);
      }

      // Load certificates
      try {
        const certificatesRes = await api.get(`/users/${userId}/certificates`);
        if (certificatesRes.data.success) {
          setCertificates(certificatesRes.data.data.certificates);
        }
      } catch (err) {
        console.error('Failed to load certificates:', err);
      }

    } catch (error) {
      console.error('Failed to load user profile:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFollow = async () => {
    if (!id) return;
    
    try {
      // If already following, unfollow
      if (followStatus?.isFollowing) {
        setUserProfile((prev: any) => ({
          ...prev,
          isFollowing: false,
          stats: {
            ...prev.stats,
            followers: prev.stats.followers - 1
          }
        }));

        const response = await api.post(`/users/${id}/follow`);
        if (response.data.success) {
          setFollowStatus({
            followStatus: 'none',
            isFollowing: false,
            hasPendingRequest: false,
            isRequestSentByMe: false,
            isRequestReceived: false
          });
        }
        return;
      }

      // Send follow request
      const response = await api.post(`/users/${id}/follow`);
      if (response.data.success) {
        // Reload follow status to get the latest state
        await loadFollowStatus(id!);
        toast.success('Follow request sent!');
      }
    } catch (error: any) {
      console.error('Failed to send follow request:', error);
      toast.error(error.response?.data?.error?.message || 'Failed to send follow request');
      // Reload follow status on error to ensure UI is in sync
      if (id) {
        await loadFollowStatus(id);
      }
    }
  };

  const handleAcceptFollow = async () => {
    if (!id) return;
    
    try {
      const response = await api.post(`/users/${id}/follow/accept`);
      if (response.data.success) {
        // Update follow status
        setFollowStatus({
          followStatus: 'following',
          isFollowing: true,
          hasPendingRequest: false,
          isRequestSentByMe: false,
          isRequestReceived: false
        });

        // Update user profile
        setUserProfile((prev: any) => ({
          ...prev,
          isFollowing: true,
          stats: {
            ...prev.stats,
            followers: prev.stats.followers + 1
          }
        }));

        toast.success('Follow request accepted!');

        // Reload follow status
        if (id) {
          await loadFollowStatus(id);
        }

        // Auto-navigate to chat if chat was created
        if (response.data.data.chat) {
          setTimeout(() => {
            navigate(`/chats/${response.data.data.chat.id}`);
            toast.success('Chat opened!');
          }, 500);
        }
      }
    } catch (error: any) {
      console.error('Failed to accept follow request:', error);
      toast.error(error.response?.data?.error?.message || 'Failed to accept follow request');
    }
  };

  const handleRejectFollow = async () => {
    if (!id) return;
    
    try {
      const response = await api.post(`/users/${id}/follow/reject`);
      if (response.data.success) {
        // Reload follow status
        await loadFollowStatus(id);
        toast.success('Follow request rejected');
      }
    } catch (error: any) {
      console.error('Failed to reject follow request:', error);
      toast.error(error.response?.data?.error?.message || 'Failed to reject follow request');
      // Reload follow status on error
      if (id) {
        await loadFollowStatus(id);
      }
    }
  };

  const handleLikePost = async (postId: string) => {
    try {
      // Optimistic update
      setPosts((prev: any[]) => prev.map(post => 
        post.id === postId 
          ? {
              ...post,
              isLiked: !post.isLiked,
              likes: post.isLiked ? post.likes - 1 : post.likes + 1
            }
          : post
      ));

      // Real API call
      await api.post(`/posts/${postId}/like`);
    } catch (error) {
      console.error('Failed to like post:', error);
      // Revert optimistic update on error
      setPosts((prev: any[]) => prev.map(post => 
        post.id === postId 
          ? {
              ...post,
              isLiked: !post.isLiked,
              likes: post.isLiked ? post.likes + 1 : post.likes - 1
            }
          : post
      ));
    }
  };

  const getSportBadgeColor = (sport: string) => {
    const colors = {
      'Chess': 'bg-red-100 text-red-800',
      'Cricket': 'bg-red-100 text-red-800',
      'Badminton': 'bg-yellow-100 text-yellow-800',
      'Football': 'bg-blue-100 text-blue-800',
      'Tennis': 'bg-green-100 text-green-800'
    };
    return colors[sport as keyof typeof colors] || 'bg-gray-100 text-gray-800';
  };

  const getRoleBadgeColor = (role: string) => {
    const colors = {
      'Player': 'bg-blue-100 text-blue-800',
      'Coach': 'bg-green-100 text-green-800',
      'Admin': 'bg-yellow-100 text-yellow-800'
    };
    return colors[role as keyof typeof colors] || 'bg-gray-100 text-gray-800';
  };

  const getAchievementColor = (level: string) => {
    const colors = {
      'gold': 'bg-yellow-100 text-yellow-800 border-yellow-200',
      'silver': 'bg-gray-100 text-gray-800 border-gray-200',
      'bronze': 'bg-orange-100 text-orange-800 border-orange-200'
    };
    return colors[level as keyof typeof colors] || 'bg-gray-100 text-gray-800 border-gray-200';
  };

  const getInitials = (name: string) => {
    return name.split(' ').map(n => n[0]).join('').toUpperCase();
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      year: 'numeric'
    });
  };

  const handleAvatarClick = () => {
    if (userProfile.isOwnProfile && fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleAvatarChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      setUploadingAvatar(true);
      const res = await uploadService.uploadImages([file]);
      if (!res?.success || !res?.data?.url) {
        const detail = res?.error?.message || res?.error?.details || 'No URL returned from upload';
        throw new Error(`Image upload failed: ${detail}`);
      }
      const avatarUrl = res.data.url;

      const updateRes = await api.put('/users/profile', { profilePicture: avatarUrl });
      if (!updateRes.data?.success) {
        throw new Error(updateRes.data?.error?.message || 'Profile save failed after upload');
      }
      setUserProfile(prev => ({ ...prev, avatar: avatarUrl }));
      if (currentUser) setUserData({ ...currentUser, avatar: avatarUrl });
      toast.success('Profile picture updated!');
    } catch (error: any) {
      const apiMsg = error?.response?.data?.error?.message
        || error?.response?.data?.error?.details
        || error?.message
        || 'Unknown error';
      console.error('[avatar upload]', { status: error?.response?.status, body: error?.response?.data, error });
      toast.error(`Profile picture upload failed: ${apiMsg}`);
    } finally {
      setUploadingAvatar(false);
    }
  };

  const handleUpdateProfile = async () => {
    try {
      setIsUpdatingProfile(true);
      // Only send fields the edit modal actually exposes. Sending sports here
      // would wipe the user's sport selection because the modal has no input
      // for it (sports is managed via the onboarding flow).
      const payload = {
        firstName: editForm.firstName,
        lastName: editForm.lastName,
        bio: editForm.bio,
        location: editForm.location,
      };
      const res = await api.put('/users/profile', payload);
      if (res.data.success) {
        // Update AuthContext so the rest of the app reflects changes
        if (res.data.data?.user && currentUser) {
          setUserData({
            ...currentUser,
            firstName: res.data.data.user.firstName,
            lastName: res.data.data.user.lastName,
            name: res.data.data.user.name,
            avatar: res.data.data.user.avatar,
            sports: res.data.data.user.sports,
          });
        }
        toast.success('Profile updated!');
        setShowEditModal(false);
        if (id) loadUserProfile(id);
      }
    } catch (error) {
      console.error('Failed to update profile:', error);
      toast.error('Failed to update profile');
    } finally {
      setIsUpdatingProfile(false);
    }
  };

  const openNetworkModal = async (type: 'followers' | 'following') => {
    try {
      setShowFollowersModal({ show: true, type });
      setNetworkUsers([]);
      setLoading(true);
      const res = await api.get(`/users/${id}/${type}`);
      if (res.data.success) {
        setNetworkUsers(res.data.data[type]);
      }
    } catch (error) {
      console.error(`Failed to load ${type}:`, error);
    } finally {
      setLoading(false);
    }
  };

  if (loading && !showFollowersModal.show) {
    return (
      <div className="flex items-center justify-center min-h-[50vh]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto bg-white min-h-[calc(100vh-120px)] sm:min-h-[calc(100vh-60px)]">
      {/* Header */}
      <div className="flex items-center justify-between p-3 sm:p-4 border-b border-gray-200">
        <button
          onClick={() => navigate(-1)}
          className="p-2 text-gray-600 hover:text-gray-800"
        >
          <ArrowLeft className="w-5 h-5 sm:w-6 sm:h-6" />
        </button>
        <h1 className="text-base sm:text-lg font-semibold text-gray-900">Profile</h1>
        {userProfile.isOwnProfile ? (
          <button 
            onClick={() => setShowEditModal(true)}
            className="p-2 text-gray-600 hover:text-gray-800"
          >
            <Settings className="w-5 h-5 sm:w-6 sm:h-6" />
          </button>
        ) : (
          <div className="w-10 h-6"></div>
        )}
      </div>

      {/* Profile Picture Upload Input (Hidden) */}
      <input 
        type="file" 
        ref={fileInputRef} 
        onChange={handleAvatarChange} 
        className="hidden" 
        accept="image/*" 
      />

      {/* Profile Header */}
      <div className="p-4 sm:p-6">
        <div className="flex items-start space-x-3 sm:space-x-4 mb-4">
          {/* Avatar */}
          <div className="relative">
            <div 
              onClick={handleAvatarClick}
              className={`w-20 h-20 sm:w-28 sm:h-28 rounded-full bg-pink-500 text-white flex items-center justify-center text-xl sm:text-3xl font-bold flex-shrink-0 relative overflow-hidden ${userProfile.isOwnProfile ? 'cursor-pointer group' : ''}`}
            >
              {userProfile.avatar ? (
                <img
                  src={userProfile.avatar}
                  alt={userProfile.name}
                  className="w-full h-full rounded-full object-cover"
                />
              ) : (
                getInitials(userProfile.name)
              )}
              {userProfile.isOwnProfile && (
                <div className="absolute inset-0 bg-black bg-opacity-40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                  {uploadingAvatar ? (
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white"></div>
                  ) : (
                    <Camera className="w-8 h-8 text-white" />
                  )}
                </div>
              )}
            </div>
            {userProfile.sport && userProfile.sport !== 'General' && (
              <div className="absolute -bottom-1 -right-1 bg-white rounded-full shadow-lg flex items-center justify-center border-4 border-white w-8 h-8 sm:w-12 sm:h-12 text-lg sm:text-2xl z-20">
                {getSportIcon(userProfile.sport)}
              </div>
            )}
          </div>


          {/* User Info */}
          <div className="flex-1 min-w-0">
            <div className="flex flex-wrap items-center gap-1 sm:gap-2 mb-2">
              <h2 className="text-lg sm:text-xl font-bold text-gray-900">{userProfile.name}</h2>
              <span className={`px-1.5 sm:px-2 py-0.5 sm:py-1 text-xs font-medium rounded-full ${getRoleBadgeColor(userProfile.role)} whitespace-nowrap`}>
                {userProfile.role}
              </span>
              <span className={`px-1.5 sm:px-2 py-0.5 sm:py-1 text-xs font-medium rounded-full ${getSportBadgeColor(userProfile.sport)} whitespace-nowrap`}>
                {userProfile.sport}
              </span>
            </div>

            {/* Badges Display */}
            {(userProfile as any).badges && (userProfile as any).badges.length > 0 && (
              <div className="flex flex-wrap items-center gap-2 mb-2 sm:mb-3">
                {(userProfile as any).badges.filter((badge: any) => badge.isActive).map((badge: any, index: number) => (
                  <div
                    key={index}
                    className={`inline-flex items-center space-x-1.5 px-2.5 py-1.5 rounded-full text-xs font-medium ${
                      badge.color === 'gold' ? 'bg-gradient-to-r from-yellow-400 to-yellow-600 text-white' :
                      badge.color === 'silver' ? 'bg-gradient-to-r from-gray-300 to-gray-500 text-white' :
                      badge.color === 'bronze' ? 'bg-gradient-to-r from-orange-400 to-orange-600 text-white' :
                      badge.color === 'blue' ? 'bg-gradient-to-r from-blue-400 to-blue-600 text-white' :
                      badge.color === 'purple' ? 'bg-gradient-to-r from-purple-400 to-purple-600 text-white' :
                      badge.color === 'green' ? 'bg-gradient-to-r from-green-400 to-green-600 text-white' :
                      'bg-gradient-to-r from-red-400 to-red-600 text-white'
                    } shadow-md hover:shadow-lg transition-shadow`}
                    title={badge.earnedFor || badge.description || badge.title}
                  >
                    <span>{badge.icon || '🏆'}</span>
                    <span>{badge.title}</span>
                    {badge.earnedFor && (
                      <span className="opacity-80 text-[10px]">• {badge.earnedFor}</span>
                    )}
                  </div>
                ))}
              </div>
            )}

            {userProfile.bio && (
              <p className="text-gray-600 text-xs sm:text-sm mb-2 sm:mb-3 break-words">{userProfile.bio}</p>
            )}

            {userProfile.location && (
              <div className="flex items-center space-x-1 text-xs sm:text-sm text-gray-500 mb-2 sm:mb-3">
                <MapPin className="w-3 h-3 sm:w-4 sm:h-4 flex-shrink-0" />
                <span className="truncate">{userProfile.location}</span>
              </div>
            )}

            <div className="flex items-center space-x-1 text-xs sm:text-sm text-gray-500">
              <Calendar className="w-3 h-3 sm:w-4 sm:h-4 flex-shrink-0" />
              <span>Joined {formatDate(userProfile.joinedDate)}</span>
            </div>
          </div>
        </div>

        {/* Stats */}
        <div className="flex items-center justify-around sm:justify-start sm:space-x-6 mb-4">
          <button onClick={() => openNetworkModal('followers')} className="text-center hover:bg-gray-50 p-2 rounded-lg transition-colors">
            <div className="text-base sm:text-lg font-bold text-gray-900">{userProfile.stats.followers.toLocaleString()}</div>
            <div className="text-xs sm:text-sm text-gray-500">Followers</div>
          </button>
          <button onClick={() => openNetworkModal('following')} className="text-center hover:bg-gray-50 p-2 rounded-lg transition-colors">
            <div className="text-base sm:text-lg font-bold text-gray-900">{userProfile.stats.following.toLocaleString()}</div>
            <div className="text-xs sm:text-sm text-gray-500">Following</div>
          </button>
          <button className="text-center">
            <div className="text-base sm:text-lg font-bold text-gray-900">{userProfile.stats.posts}</div>
            <div className="text-xs sm:text-sm text-gray-500">Posts</div>
          </button>
        </div>

        {/* Follow Button / Follow Request Actions */}
        {!userProfile.isOwnProfile && (
          <div className="flex flex-col sm:flex-row gap-2 w-full">
            {followStatus?.isRequestReceived ? (
              // Show Accept/Reject buttons if request received
              <>
                <button
                  onClick={handleAcceptFollow}
                  className="flex-1 py-2.5 sm:py-3 bg-blue-500 text-white rounded-lg text-sm sm:text-base font-medium hover:bg-blue-600 transition-colors flex items-center justify-center space-x-2"
                >
                  <UserCheck className="w-4 h-4 sm:w-5 sm:h-5" />
                  <span>Accept</span>
                </button>
                <button
                  onClick={handleRejectFollow}
                  className="flex-1 py-2.5 sm:py-3 bg-gray-100 text-gray-700 rounded-lg text-sm sm:text-base font-medium hover:bg-gray-200 transition-colors flex items-center justify-center space-x-2"
                >
                  <span>Reject</span>
                </button>
              </>
            ) : followStatus?.isRequestSentByMe ? (
              // Show "Request Sent" if request sent by current user
              <button
                disabled
                className="w-full py-2.5 sm:py-3 bg-gray-100 text-gray-500 rounded-lg text-sm sm:text-base font-medium cursor-not-allowed flex items-center justify-center space-x-2"
              >
                <UserPlus className="w-4 h-4 sm:w-5 sm:h-5" />
                <span>Request Sent</span>
              </button>
            ) : followStatus?.isFollowing ? (
              // Show "Following" button if already following
              <button
                onClick={handleFollow}
                className="w-full py-2.5 sm:py-3 bg-gray-100 text-gray-700 rounded-lg text-sm sm:text-base font-medium hover:bg-gray-200 transition-colors flex items-center justify-center space-x-2"
              >
                <UserCheck className="w-4 h-4 sm:w-5 sm:h-5" />
                <span>Following</span>
              </button>
            ) : (
              // Show "Follow" button if not following
              <button
                onClick={handleFollow}
                className="w-full py-2.5 sm:py-3 bg-blue-500 text-white rounded-lg text-sm sm:text-base font-medium hover:bg-blue-600 transition-colors flex items-center justify-center space-x-2"
              >
                <UserPlus className="w-4 h-4 sm:w-5 sm:h-5" />
                <span>Follow</span>
              </button>
            )}
          </div>
        )}
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 overflow-x-auto scrollbar-hide">
        <div className="flex space-x-4 sm:space-x-8 px-4 sm:px-6 min-w-max">
          {['posts', 'achievements', 'certificates'].map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab as any)}
              className={`py-3 text-xs sm:text-sm font-medium capitalize border-b-2 transition-colors whitespace-nowrap ${
                activeTab === tab
                  ? 'border-black text-black'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              {tab}
            </button>
          ))}
        </div>
      </div>

      {/* Tab Content */}
      <div className="p-4 sm:p-6">
        {activeTab === 'posts' && (
          <div className="space-y-4 sm:space-y-6">
            {posts.length > 0 ? (
              posts.map((post) => (
                <div key={post.id} className={`bg-white rounded-lg border ${(post as any).type === 'achievement' ? 'border-yellow-200' : (post as any).type === 'certificate' ? 'border-amber-200' : 'border-gray-200'}`}>
                  {/* Post Header */}
                  <div className="p-3 sm:p-4 flex items-start sm:items-center justify-between gap-2">
                    <div className="flex items-center space-x-2 sm:space-x-3 flex-1 min-w-0">
                      <div className="relative w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-pink-500 text-white flex items-center justify-center text-xs sm:text-sm font-medium flex-shrink-0">
                        {post.user.avatar ? (
                          <img
                            src={post.user.avatar}
                            alt={post.user.name}
                            className="w-full h-full rounded-full object-cover"
                          />
                        ) : (
                          getInitials(post.user.name)
                        )}
                        {post.user.sport && post.user.sport !== 'General' && (
                          <div className="absolute -bottom-1 -right-1 bg-white rounded-full shadow-md flex items-center justify-center border border-white w-4 h-4 sm:w-5 sm:h-5 text-[8px] sm:text-[10px] z-10">
                            {getSportIcon(post.user.sport)}
                          </div>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex flex-wrap items-center gap-1 sm:gap-2">
                          <h4 className="font-semibold text-gray-900 text-sm sm:text-base truncate">{post.user.name}</h4>
                          <span className={`px-1.5 sm:px-2 py-0.5 text-xs font-medium rounded-full ${getRoleBadgeColor(post.user.role)} whitespace-nowrap`}>
                            {post.user.role}
                          </span>
                          <span className={`px-1.5 sm:px-2 py-0.5 text-xs font-medium rounded-full ${getSportBadgeColor(post.user.sport)} whitespace-nowrap`}>
                            {post.user.sport}
                          </span>
                          {post.location && (
                            <span className="flex items-center space-x-1 text-gray-500 text-[10px] sm:text-xs">
                              <MapPin className="w-2.5 h-2.5 sm:w-3 sm:h-3 text-gray-400" />
                              <span className="truncate max-w-[100px] sm:max-w-[150px]">{post.location}</span>
                            </span>
                          )}
                        </div>
                        <div className="flex flex-col">
                          <p className="text-xs sm:text-sm text-gray-500 mt-0.5">
                            {post.timeAgo}
                          </p>
                          {post.tags && post.tags.length > 0 && (
                            <div className="flex flex-wrap items-center gap-1 mt-0.5">
                              <span className="text-[10px] sm:text-xs text-gray-400">with</span>
                              {post.tags.map((tag: any) => (
                                <button
                                  key={tag.id}
                                  onClick={() => navigate(`/profile/${tag.id}`)}
                                  className="text-[10px] sm:text-xs font-medium text-blue-500 hover:text-blue-700 hover:underline"
                                >
                                  @{tag.name}
                                </button>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                    <button className="p-1 sm:p-2 text-gray-400 hover:text-gray-600 flex-shrink-0">
                      <MoreHorizontal className="w-4 h-4 sm:w-5 sm:h-5" />
                    </button>
                  </div>

                  {/* Achievement / Certificate type badge */}
                  {(post as any).type === 'achievement' && (
                    <div className="px-3 sm:px-4 pt-2 pb-0">
                      <div className="flex items-center space-x-1.5 px-2.5 py-1 bg-yellow-50 border border-yellow-200 rounded-full w-fit">
                        <Trophy className="w-3 h-3 text-yellow-600" />
                        <span className="text-xs font-bold text-yellow-700 uppercase tracking-wide">Achievement</span>
                      </div>
                    </div>
                  )}
                  {(post as any).type === 'certificate' && (
                    <div className="px-3 sm:px-4 pt-2 pb-0">
                      <div className="flex items-center space-x-1.5 px-2.5 py-1 bg-amber-50 border border-amber-200 rounded-full w-fit">
                        <Award className="w-3 h-3 text-amber-600" />
                        <span className="text-xs font-bold text-amber-700 uppercase tracking-wide">Certificate</span>
                      </div>
                    </div>
                  )}

                  {/* Post Content */}
                  <div className="px-3 sm:px-4 pb-2 sm:pb-3">
                    <p className="text-sm sm:text-base text-gray-900 break-words">{post.content}</p>
                  </div>

                  {/* Achievement inline card */}
                  {(post as any).type === 'achievement' && (post as any).achievement && (
                    <div className="mx-3 sm:mx-4 mb-3 p-3 bg-gradient-to-r from-yellow-50 to-amber-50 border border-yellow-200 rounded-lg">
                      <div className="flex items-start space-x-2.5">
                        <div className="p-2 bg-gradient-to-br from-yellow-400 to-amber-500 rounded-full flex-shrink-0">
                          <Trophy className="w-4 h-4 text-white" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex flex-wrap items-center gap-1.5 mb-0.5">
                            <span className="font-semibold text-gray-900 text-sm">{(post as any).achievement.title}</span>
                            {(post as any).achievement.verified && (
                              <span className="px-1.5 py-0.5 bg-green-100 text-green-700 text-xs font-medium rounded-full">✓ Verified</span>
                            )}
                          </div>
                          {(post as any).achievement.description && (
                            <p className="text-xs text-gray-600">{(post as any).achievement.description}</p>
                          )}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Certificate inline card */}
                  {(post as any).type === 'certificate' && (post as any).certificate && (
                    <div className="mx-3 sm:mx-4 mb-3 p-3 bg-gradient-to-r from-amber-50 to-yellow-50 border border-amber-200 rounded-lg">
                      <div className="flex items-start space-x-2.5">
                        <div className="p-2 bg-gradient-to-br from-amber-400 to-yellow-500 rounded-full flex-shrink-0">
                          <Award className="w-4 h-4 text-white" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex flex-wrap items-center gap-1.5 mb-0.5">
                            <span className="font-semibold text-gray-900 text-sm">{(post as any).certificate.title}</span>
                            {(post as any).certificate.verified && (
                              <span className="px-1.5 py-0.5 bg-green-100 text-green-700 text-xs font-medium rounded-full">✓ Verified</span>
                            )}
                          </div>
                          {(post as any).certificate.issuer && (
                            <p className="text-xs text-gray-500"><span className="font-medium text-amber-700">Issued by:</span> {(post as any).certificate.issuer}</p>
                          )}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Post Image */}
                  {post.image && (
                    <div className="px-3 sm:px-4 pb-2 sm:pb-3">
                      <img
                        src={post.image}
                        alt="Post content"
                        className="w-full h-48 sm:h-64 object-cover rounded-lg"
                      />
                    </div>
                  )}

                  {/* Post Stats */}
                  <div className="px-3 sm:px-4 py-2 border-t border-gray-100">
                    <div className="flex items-center justify-between text-xs sm:text-sm text-gray-500">
                      <span>{post.likes} likes</span>
                      <div className="flex space-x-3 sm:space-x-4">
                        <span>{post.comments} comments</span>
                        <span>{post.shares} shares</span>
                      </div>
                    </div>
                  </div>

                  {/* Post Actions */}
                  <div className="px-3 sm:px-4 py-2 sm:py-3 border-t border-gray-100">
                    <div className="flex items-center justify-around gap-1">
                      <button
                        onClick={() => handleLikePost(post.id)}
                        className={`flex items-center space-x-1 sm:space-x-2 px-2 sm:px-4 py-1.5 sm:py-2 rounded-lg transition-colors flex-1 justify-center ${
                          post.isLiked 
                            ? 'text-red-600 bg-red-50' 
                            : 'text-gray-600 hover:bg-gray-50'
                        }`}
                      >
                        <Heart className={`w-4 h-4 sm:w-5 sm:h-5 ${post.isLiked ? 'fill-current' : ''}`} />
                        <span className="text-xs sm:text-sm font-medium hidden sm:inline">Like</span>
                      </button>
                      <button 
                        onClick={() => setShowComments(post.id)}
                        className="flex items-center space-x-1 sm:space-x-2 px-2 sm:px-4 py-1.5 sm:py-2 rounded-lg text-gray-600 hover:bg-gray-50 transition-colors flex-1 justify-center"
                      >
                        <MessageCircle className="w-4 h-4 sm:w-5 sm:h-5" />
                        <span className="text-xs sm:text-sm font-medium hidden sm:inline">Comment</span>
                      </button>
                      <button 
                        onClick={() => setShowShareModal(post.id)}
                        className="flex items-center space-x-1 sm:space-x-2 px-2 sm:px-4 py-1.5 sm:py-2 rounded-lg text-gray-600 hover:bg-gray-50 transition-colors flex-1 justify-center"
                      >
                        <Share className="w-4 h-4 sm:w-5 sm:h-5" />
                        <span className="text-xs sm:text-sm font-medium hidden sm:inline">Share</span>
                      </button>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center py-8 sm:py-12">
                <Grid3X3 className="w-12 h-12 sm:w-16 sm:h-16 text-gray-300 mx-auto mb-3 sm:mb-4" />
                <h3 className="text-base sm:text-lg font-medium text-gray-900 mb-2">No posts yet</h3>
                <p className="text-sm sm:text-base text-gray-500">
                  {userProfile.isOwnProfile ? 'Share your first post!' : 'No posts to show'}
                </p>
              </div>
            )}
          </div>
        )}

        {activeTab === 'achievements' && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
            {achievements.length > 0 ? (
              achievements.map((achievement) => (
                <AchievementCard
                  key={achievement.id}
                  achievement={achievement}
                  formatDate={formatDate}
                />
              ))
            ) : (
              <div className="col-span-2 text-center py-12">
                <Trophy className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">No achievements yet</h3>
                <p className="text-gray-500">
                  {userProfile.isOwnProfile ? 'Start competing to earn achievements!' : 'No achievements to show'}
                </p>
              </div>
            )}
          </div>
        )}

        {activeTab === 'certificates' && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
            {certificates.length > 0 ? (
              certificates.map((certificate) => (
                <CertificateCard
                  key={certificate.id}
                  certificate={certificate}
                  formatDate={formatDate}
                />
              ))
            ) : (
              <div className="col-span-2 text-center py-12">
                <Award className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">No certificates yet</h3>
                <p className="text-gray-500">
                  {userProfile.isOwnProfile ? 'Complete training programs to earn certificates!' : 'No certificates to show'}
                </p>
              </div>
            )}
          </div>
        )}
      </div>


      {/* Edit Profile Modal */}
      {showEditModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-md p-6 space-y-4">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-gray-900">Edit Profile</h2>
              <button onClick={() => setShowEditModal(false)} className="p-1 hover:bg-gray-100 rounded-full">
                <XIcon className="w-6 h-6 text-gray-500" />
              </button>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1">
                <label className="text-xs font-semibold text-gray-600 uppercase">First Name</label>
                <input
                  type="text"
                  value={editForm.firstName}
                  onChange={(e) => setEditForm(prev => ({ ...prev, firstName: e.target.value }))}
                  className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                />
              </div>
              <div className="space-y-1">
                <label className="text-xs font-semibold text-gray-600 uppercase">Last Name</label>
                <input
                  type="text"
                  value={editForm.lastName}
                  onChange={(e) => setEditForm(prev => ({ ...prev, lastName: e.target.value }))}
                  className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                />
              </div>
            </div>

            <div className="space-y-1">
              <label className="text-xs font-semibold text-gray-600 uppercase">Bio</label>
              <textarea
                value={editForm.bio}
                onChange={(e) => setEditForm(prev => ({ ...prev, bio: e.target.value }))}
                className="w-full p-2 border border-gray-300 rounded-lg h-24 focus:ring-2 focus:ring-blue-500 outline-none resize-none"
              />
            </div>

            <div className="space-y-1">
              <label className="text-xs font-semibold text-gray-600 uppercase">Location</label>
              <div className="relative">
                <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  value={editForm.location}
                  onChange={(e) => setEditForm(prev => ({ ...prev, location: e.target.value }))}
                  className="w-full pl-9 p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                />
              </div>
            </div>

            <button
              onClick={handleUpdateProfile}
              disabled={isUpdatingProfile}
              className="w-full py-3 bg-blue-600 text-white font-bold rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center justify-center space-x-2"
            >
              {isUpdatingProfile ? (
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
              ) : (
                <>
                  <Save className="w-5 h-5" />
                  <span>Save Changes</span>
                </>
              )}
            </button>
          </div>
        </div>
      )}

      {/* Followers/Following Modal */}
      {showFollowersModal.show && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-sm max-h-[80vh] flex flex-col">
            <div className="p-4 border-b border-gray-100 flex items-center justify-between">
              <h2 className="text-lg font-bold capitalize">{showFollowersModal.type}</h2>
              <button onClick={() => setShowFollowersModal({ ...showFollowersModal, show: false })} className="p-1 hover:bg-gray-100 rounded-full">
                <XIcon className="w-5 h-5 text-gray-500" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-2">
              {loading ? (
                <div className="flex justify-center p-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                </div>
              ) : networkUsers.length > 0 ? (
                networkUsers.map(u => (
                  <div key={u.id} onClick={() => { navigate(`/profile/${u.id}`); setShowFollowersModal({ ...showFollowersModal, show: false }); }} className="flex items-center space-x-3 p-3 hover:bg-gray-50 rounded-xl cursor-pointer">
                    <div className="relative">
                      <div className="w-10 h-10 rounded-full bg-blue-500 text-white flex items-center justify-center font-bold overflow-hidden">
                        {u.avatar ? <img src={u.avatar} className="w-full h-full rounded-full object-cover" /> : u.name[0]}
                      </div>
                      {u.sport && u.sport !== 'General' && (
                        <div className="absolute -bottom-1 -right-1 bg-white rounded-full shadow-md flex items-center justify-center border-2 border-white w-5 h-5 text-[12px] z-10">
                          {getSportIcon(u.sport)}
                        </div>
                      )}
                    </div>
                    <div className="flex-1">

                      <div className="text-sm font-bold text-gray-900">{u.name}</div>
                      <div className="text-xs text-gray-500">{u.role} • {u.sport}</div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-center py-8 text-gray-500">No {showFollowersModal.type} yet</div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Share Modal */}
      {showShareModal && (
        <ShareModal
          postId={showShareModal}
          post={posts.find(p => p.id === showShareModal)}
          onClose={() => setShowShareModal(null)}
        />
      )}

      {/* Comments Section */}
      <CommentsSection
        postId={showComments || ''}
        isOpen={!!showComments}
        onClose={() => setShowComments(null)}
      />
    </div>
  );
};

export default ProfilePage;